/**
 * EmployeeFormModal — karta pracownika (create/edit) osadzona w oknie modalnym, NAD oknem
 * wyboru rekordu (akcje „Dodaj nowego" / „Edytuj" w tabeli pola relacji do Pracowników).
 * Mirror `ContractorFormModal`: zamiast osobnej formatki reużywa pełnej karty
 * (`EmployeeDetail embedded`) — jedno źródło prawdy dla strony i okna.
 *
 * `onSaved(id)` woła się po udanym zapisie (create → id nowego rekordu, edit → id
 * edytowanego); co dalej, decyduje okno-gospodarz.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/Modal';
import { EmployeeDetail } from '@/pages/EmployeeDetail';

export interface EmployeeFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  /** Wymagane w trybie edit — pracownik do edycji. */
  employeeId?: string;
  /** Firma rekordu-gospodarza (prefill firmy w create). */
  companyId?: string;
  onClose: () => void;
  /** Po udanym zapisie — `id` zapisanego rekordu (create i edit). */
  onSaved: (id: string) => void;
  'data-testid'?: string;
}

export function EmployeeFormModal({
  open,
  mode,
  employeeId,
  companyId,
  onClose,
  onSaved,
  'data-testid': testId,
}: EmployeeFormModalProps) {
  const { t } = useTranslation();
  // Edycja zostaje otwarta po zapisie — zapis zeruje „coś wpisano", żeby Esc zaraz po zapisie
  // nie pytał o porzucenie zmian, które już zapisano.
  const [savedRev, setSavedRev] = useState(0);
  const handleSaved = (id: string) => {
    setSavedRev((rev) => rev + 1);
    onSaved(id);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="2xl"
      fixedHeight
      expandable
      guardUnsaved
      guardResetKey={savedRev}
      title={
        mode === 'create'
          ? t('tenant.employees.formModal.createTitle')
          : t('tenant.employees.formModal.editTitle')
      }
    >
      <div data-testid={testId ?? 'employee-form-modal'}>
        <EmployeeDetail
          embedded={{ mode, employeeId, companyId, onSaved: handleSaved, onCancel: onClose }}
        />
      </div>
    </Modal>
  );
}
